/**
 * RoutingDataCard - the offline routing package on this computer.
 *
 * TCA-26100804 closeout. Converted to match the offline map card beside it: the
 * tab's status slot owns the sentence for both packages, and each card owns
 * identity, versions and controls. This card previously carried its own tone
 * badge and its own status lines, which left two visual languages on one tab
 * and a second place for the same state to be reported.
 *
 * `servicesUnavailable` fences both controls. It was missing here while the map
 * controls beside it were fenced, so stopping the local services disabled one
 * half of the tab and left the other half clickable.
 *
 * The wording distinction that matters: a machine with no routing data has an
 * outstanding setup step, not a fault. Presenting that as an error makes every
 * new install look broken on first run. See TCA-26081002.
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Route } from 'lucide-react';
import { describeRoutingState } from '@/api/offlineRoutingAssets';

const ROLLBACK_NOTE = 'Roll back only if the current routing data causes problems. A confirmation opens first.';

const RoutingDataCard = ({
  updateStatus, phase = 'ready', busy = false, outcome = null, onRefresh,
  loading,
  installing,
  rollingBack,
  onInstall,
  onRollback,
  servicesUnavailable = false,
}) => {
  const state = describeRoutingState(updateStatus);
  const local = updateStatus?.local || {};
  const published = updateStatus?.published || null;

  return (
    <div
      className="rounded-xl border border-stone-200 bg-white px-4 py-3"
      data-testid="routing-data-status"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100">
          <Route className="h-4 w-4 text-primary" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-stone-800">Offline routing data</p>
          <p className="mt-0.5 text-xs text-stone-500">
            Route lines for dispatch without a connection.
          </p>

          {!loading && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-500">
              <span data-testid="routing-data-installed-version">
                Installed: {local.version || (local.state === 'installed_unmanaged' ? 'unmanaged copy' : local.state === 'not_installed' ? 'none' : 'unknown')}
              </span>
              <span data-testid="routing-data-published-version">
                Published: {published?.version || 'unknown'}
              </span>
            </div>
          )}

          {(phase === 'unavailable' || outcome === 'unknown' || outcome === 'handoff') && <div role="status" className="mt-2 text-sm">
            {outcome === 'unknown' ? 'Routing staging or restart is unconfirmed. Do not repeat the request; follow the desktop recovery guidance or contact support.'
              : outcome === 'handoff' ? 'Routing data is staged for restart; installation is not yet confirmed.' : 'Current routing status could not be read. Displayed versions are last known.'}
            <Button variant="outline" disabled={loading} onClick={onRefresh}>Refresh routing status</Button>
          </div>}
          {state.canInstall && (
            <Button
              variant="outline"
              size="sm"
              onClick={onInstall}
              disabled={loading || busy || installing || rollingBack || phase !== 'ready' || servicesUnavailable}
              className="mt-3 w-full h-9 text-xs"
              data-testid="routing-data-action"
            >
              {installing
                ? 'Installing…'
                : (local.state === 'not_installed' ? 'Install routing data' : 'Update routing data')}
            </Button>
          )}

          {state.canRollback && (
            <div className="mt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onRollback}
                disabled={loading || busy || installing || rollingBack || phase !== 'ready' || servicesUnavailable}
                className="w-full h-9 text-xs border-rose-200 text-rose-700 hover:text-rose-800"
                data-testid="routing-data-rollback"
              >
                {rollingBack ? 'Restoring…' : 'Rollback routing data'}
              </Button>
              <p className="mt-1 text-[11px] text-stone-500">{ROLLBACK_NOTE}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoutingDataCard;
